/**
 * Payment Complete Page
 * Handles Stripe redirect after payment confirmation
 * L0-CMD-2026-0120-007
 */

import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';

type PaymentStatus = 'loading' | 'success' | 'processing' | 'error';

const PaymentComplete: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<PaymentStatus>('loading');
  const [message, setMessage] = useState('');
  const [projectId, setProjectId] = useState<string | null>(null);

  const API_BASE = process.env.REACT_APP_API_URL || '';

  useEffect(() => {
    const paymentIntent = searchParams.get('payment_intent');
    const paymentIntentClientSecret = searchParams.get('payment_intent_client_secret');
    const redirectStatus = searchParams.get('redirect_status');
    const project = searchParams.get('project');

    setProjectId(project);

    if (redirectStatus === 'succeeded') {
      setStatus('success');
      setMessage('Your payment was successful! A receipt has been sent to your email.');

      // Confirm payment with backend
      if (paymentIntent) {
        confirmPayment(paymentIntent);
      }
    } else if (redirectStatus === 'processing') {
      setStatus('processing');
      setMessage('Your payment is being processed. You will receive a confirmation email shortly.');
    } else if (redirectStatus === 'requires_payment_method') {
      setStatus('error');
      setMessage('Payment failed. Please try again with a different payment method.');
    } else {
      setStatus('error');
      setMessage('Something went wrong. Please contact support if the issue persists.');
    }
  }, [searchParams]);

  const confirmPayment = async (paymentIntentId: string) => {
    try {
      const token = localStorage.getItem('access_token');

      await fetch(`${API_BASE}/api/payments/confirm`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          payment_intent_id: paymentIntentId
        })
      });
    } catch (err) {
      console.error('Error confirming payment:', err);
      // Don't show error to user - webhook will handle it
    }
  };

  const handleViewProjects = () => {
    navigate('/client/projects');
  };

  const handleTryAgain = () => {
    if (projectId) {
      navigate(`/client/projects?pay=${projectId}`);
    } else {
      navigate('/client/projects');
    }
  };

  return (
    <div className="payment-complete-page">
      <div className="payment-complete-card">
        {status === 'loading' && (
          <>
            <div className="status-icon loading">
              <div className="spinner"></div>
            </div>
            <h1>Processing Payment...</h1>
            <p>Please wait while we confirm your payment.</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="status-icon success">
              <span>&#10003;</span>
            </div>
            <h1>Payment Successful!</h1>
            <p>{message}</p>
            <div className="payment-info">
              <p>Your project is now in our queue and will be processed shortly.</p>
            </div>
            <button className="btn-primary" onClick={handleViewProjects}>
              View Your Projects
            </button>
          </>
        )}

        {status === 'processing' && (
          <>
            <div className="status-icon processing">
              <span>&#8987;</span>
            </div>
            <h1>Payment Processing</h1>
            <p>{message}</p>
            <div className="payment-info">
              <p>This usually takes a few minutes. We'll notify you once confirmed.</p>
            </div>
            <button className="btn-primary" onClick={handleViewProjects}>
              View Your Projects
            </button>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="status-icon error">
              <span>&#10005;</span>
            </div>
            <h1>Payment Failed</h1>
            <p>{message}</p>
            <div className="button-group">
              <button className="btn-secondary" onClick={handleViewProjects}>
                Back to Projects
              </button>
              <button className="btn-primary" onClick={handleTryAgain}>
                Try Again
              </button>
            </div>
          </>
        )}
      </div>

      <style>{`
        .payment-complete-page {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background: var(--bg-primary, #0f172a);
          padding: 20px;
        }

        .payment-complete-card {
          background: var(--bg-secondary, #1e293b);
          border-radius: 16px;
          padding: 48px;
          max-width: 480px;
          width: 100%;
          text-align: center;
          box-shadow: 0 4px 24px rgba(0, 0, 0, 0.3);
        }

        .status-icon {
          width: 96px;
          height: 96px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 24px;
          font-size: 48px;
        }

        .status-icon.success {
          background: rgba(16, 185, 129, 0.2);
          color: #10b981;
        }

        .status-icon.processing {
          background: rgba(245, 158, 11, 0.2);
          color: #f59e0b;
        }

        .status-icon.error {
          background: rgba(239, 68, 68, 0.2);
          color: #ef4444;
        }

        .status-icon.loading {
          background: rgba(37, 99, 235, 0.2);
        }

        .spinner {
          width: 48px;
          height: 48px;
          border: 3px solid rgba(37, 99, 235, 0.3);
          border-top-color: #2563eb;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        h1 {
          margin: 0 0 12px 0;
          font-size: 28px;
          color: var(--text-primary, #f1f5f9);
        }

        p {
          margin: 0 0 16px 0;
          color: var(--text-secondary, #94a3b8);
          font-size: 16px;
          line-height: 1.6;
        }

        .payment-info {
          background: var(--bg-tertiary, #334155);
          border-radius: 8px;
          padding: 16px;
          margin: 24px 0;
        }

        .payment-info p {
          margin: 0;
          font-size: 14px;
        }

        .btn-primary, .btn-secondary {
          padding: 14px 28px;
          border: none;
          border-radius: 8px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn-primary {
          background: var(--primary, #2563eb);
          color: white;
        }

        .btn-primary:hover {
          background: #1d4ed8;
        }

        .btn-secondary {
          background: var(--bg-tertiary, #334155);
          color: var(--text-primary, #f1f5f9);
        }

        .btn-secondary:hover {
          background: #475569;
        }

        .button-group {
          display: flex;
          gap: 12px;
          justify-content: center;
        }

        @media (max-width: 480px) {
          .payment-complete-card {
            padding: 32px 24px;
          }

          .button-group {
            flex-direction: column;
          }

          .btn-primary, .btn-secondary {
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
};

export default PaymentComplete;
